// hooks/useListaUsuarios.js
import { useState, useEffect } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

const toLower = (s) => (s || "").toString().toLowerCase();
// Normaliza a minúsculas y elimina tildes (para búsquedas robustas)
const normalizeText = (s = "") =>
  toLower(s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
// Deja solo dígitos (para comparar cédulas)
const digitsOnly = (s = "") => String(s).replace(/\D+/g, "");

// Subroles que deben contarse como "Docente"
const DOCENTE_SUBROLES = [
  "docente",
  "docente de planta",
  "docente de cátedra",
  "docente de catedra", // sin tilde
];

// Mapea a un grupo canónico para lógica/agrupaciones
const rolGrupo = (rol) => {
  const r = toLower(rol);
  if (DOCENTE_SUBROLES.includes(r)) return "Docente";
  if (r === "admin" || r === "administrador") return "Administrador";
  if (r === "administrativo") return "Administrativo";
  if (r === "estudiante" || r === "student") return "Estudiante";
  return rol || "Estudiante";
};

// Formatea para UI (no cambia el valor real que guardas)
const formatRol = (rol) => {
  const r = toLower(rol);
  if (r === "admin") return "Administrador";
  if (r === "administrador") return "Administrador";
  if (r === "profesor") return "Docente";
  if (r === "docente") return "Docente";
  if (r === "docente de planta") return "Docente de planta";
  if (r === "docente de cátedra" || r === "docente de catedra")
    return "Docente de cátedra";
  if (r === "administrativo") return "Administrativo";
  if (r === "estudiante" || r === "student") return "Estudiante";
  return rol || "Estudiante";
};

const useListaUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [resultados, setResultados] = useState([]);
  const [error, setError] = useState(null);
  const [usuarioEditar, setUsuarioEditar] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usuariosPorPagina] = useState(10);
  const [resultadosPorPagina] = useState(10);
  const [mostrarUsuarios, setMostrarUsuarios] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTermResultados, setSearchTermResultados] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const getAuthToken = () => localStorage.getItem("token");

  const fetchUsuarios = async () => {
    try {
      setIsLoading(true);
      const authToken = getAuthToken();
      const { data } = await axios.get(`${API_BASE_URL}/api/usuarios`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { search: searchTerm }, // backend puede buscar por nombre/correo/cédula, etc.
      });

      // Añadimos rolGrupo derivado para lógica
      const conGrupo = Array.isArray(data)
        ? data.map((u) => ({
            ...u,
            rolGrupo: rolGrupo(u?.rol),
          }))
        : [];

      setUsuarios(conGrupo);
      setTotalPages(Math.ceil(conGrupo.length / usuariosPorPagina));
    } catch (err) {
      setError("Error al cargar los usuarios");
      console.error("Error al cargar los usuarios:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchResultados = async () => {
    try {
      setIsLoading(true);
      const authToken = getAuthToken();

      // Importante: NO mandamos "search" al endpoint de resultados.
      // Traemos todo y filtramos en frontend por nombre, apellido o cédula.
      const [resultadosRes, usuariosRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/resultados/totales`, {
          headers: { Authorization: `Bearer ${authToken}` },
        }),
        axios.get(`${API_BASE_URL}/api/usuarios`, {
          headers: { Authorization: `Bearer ${authToken}` },
        }),
      ]);

      const listaUsuarios = Array.isArray(usuariosRes.data)
        ? usuariosRes.data
        : [];
      const resultadosArr = Array.isArray(resultadosRes.data)
        ? resultadosRes.data
        : [];

      // Índices para emparejar rápido por id o por nombre normalizado
      const mapById = new Map(listaUsuarios.map((u) => [u.id, u]));
      const mapByName = new Map(
        listaUsuarios.map((u) => [normalizeText(u?.nombre || ""), u])
      );

      // Enriquecer resultados con rol + apellidos + cédula reales del usuario
      const enriquecidos = resultadosArr.map((resultado) => {
        const usuarioEmp =
          mapById.get(resultado.usuarioId) ||
          mapByName.get(normalizeText(resultado.nombre || ""));

        const rolOriginal = usuarioEmp?.rol;
        const rolFormateado = formatRol(rolOriginal);
        const grupo = rolGrupo(rolOriginal);

        const apellidos = usuarioEmp?.apellidos ?? usuarioEmp?.apellido ?? "";
        const numcc =
          usuarioEmp?.numcc ??
          usuarioEmp?.cedula ??
          usuarioEmp?.documento ??
          "";

        return {
          ...resultado,
          rol: rolFormateado,
          rolGrupo: grupo,
          rolOriginal,
          apellido: apellidos,
          apellidos: apellidos,
          numcc, // ← cédula/documento para filtrar y (si quieres) mostrar
          cedula: numcc, // alias por compatibilidad
          documento: numcc, // alias
        };
      });

      // Filtro en frontend por nombre, apellidos o cédula (case/tilde-insensitive)
      const term = normalizeText(searchTermResultados);
      const termDigits = digitsOnly(searchTermResultados);
      const filtrados =
        term || termDigits
          ? enriquecidos.filter((r) => {
              const n = normalizeText(r.nombre || "");
              const a = normalizeText(r.apellido || r.apellidos || "");
              const cc = digitsOnly(r.numcc || r.cedula || r.documento || "");
              return (
                (term && (n.includes(term) || a.includes(term))) ||
                (termDigits && cc.includes(termDigits))
              );
            })
          : enriquecidos;

      setResultados(filtrados);
      setTotalPages(Math.ceil(filtrados.length / resultadosPorPagina));
    } catch (err) {
      setError("Error al cargar los resultados");
      console.error("Error al cargar los resultados:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Refetch al cambiar términos de búsqueda (y reseteo de página a 1)
  useEffect(() => {
    setCurrentPage(1);
    fetchUsuarios();
    fetchResultados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, searchTermResultados]);

  useEffect(() => {
    if (mostrarUsuarios) {
      setTotalPages(Math.ceil(usuarios.length / usuariosPorPagina));
    } else {
      setTotalPages(Math.ceil(resultados.length / resultadosPorPagina));
    }
  }, [
    mostrarUsuarios,
    usuarios,
    resultados,
    usuariosPorPagina,
    resultadosPorPagina,
  ]);

  const handleEditar = (usuario) => setUsuarioEditar(usuario);

  const handleCerrarEdicion = () => {
    setUsuarioEditar(null);
    fetchUsuarios();
  };

  const handleDownloadExcel = async (type) => {
    try {
      const authToken = getAuthToken();
      const endpoint =
        type === "usuarios"
          ? "/api/usuarios/download-excel"
          : "/api/resultados/download-excel";
      const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${type}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error(`Error al descargar el archivo Excel de ${type}:`, error);
      setError(`Error al descargar el archivo Excel de ${type}`);
    }
  };

  // En hooks/useListaUsuarios.js
  const handleDownloadCertificado = async (usuarioId, nombre, apellidos) => {
    try {
      const authToken = getAuthToken();

      // En modo admin: pasamos usuarioId para que el backend tome nombre+apellidos desde BD.
      // En modo usuario: puedes llamar sin usuarioId y el backend usará req.user.id.
      const payload = usuarioId ? { usuarioId } : {};

      const response = await axios.post(
        `${API_BASE_URL}/api/certificado/generar`,
        payload,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      // Nombre de archivo amigable (usamos los datos de la fila para el filename)
      const fullName = [nombre, apellidos].filter(Boolean).join(" ").trim();
      const filename = `certificado_${(fullName || "usuario")
        .replace(/\s+/g, "_")
        .trim()}.png`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error al descargar el certificado:", error);
      setError("Error al descargar el certificado");
    }
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return {
    usuarios,
    resultados,
    error,
    usuarioEditar,
    currentPage,
    usuariosPorPagina,
    resultadosPorPagina,
    mostrarUsuarios,
    setMostrarUsuarios,
    handleEditar,
    handleCerrarEdicion,
    handleDownloadExcel,
    handleDownloadCertificado,
    paginate,
    totalPages,
    searchTerm,
    setSearchTerm,
    searchTermResultados,
    setSearchTermResultados,
    isLoading,
  };
};

export default useListaUsuarios;
